import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../Styles/Navbar.css";
import Logo from "../assets/QuadCode-256.png";
import { useLang } from "../i18n/useLang";
import { Globe } from "lucide-react";
import { localizedPath } from "../i18n/routes";

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return false;

  const header = document.querySelector(".nv");
  const headerH = header ? header.offsetHeight : 0;

  const top = el.getBoundingClientRect().top + window.pageYOffset - headerH - 8;
  window.scrollTo({ top, behavior: "smooth" });
  return true;
}

export default function Navbar() {
  const { lang, setLang, t, dir } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const headerRef = useRef(null);
  const langRef = useRef(null);
  const hashLockUntilRef = useRef(0);

  const [activeId, setActiveId] = useState(() => {
    const hash = window.location.hash?.replace("#", "");
    return hash || "top";
  });

  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const [menuOpen, setMenuOpen] = useState(false);

  // Custom lang dropdown state
  const [langOpen, setLangOpen] = useState(false);

  const sectionIds = useMemo(
    () => ["top", "services", "process", "results", "pricing", "faq", "contact"],
    []
  );

  const navItems = useMemo(
    () => [
      { id: "services", label: t.nav.services },
      { id: "process", label: t.nav.process },
      { id: "results", label: t.nav.results },
      { id: "pricing", label: t.nav.pricing },
      { id: "faq", label: t.nav.faq },
    ],
    [t]
  );

  const langItems = useMemo(
    () => [
      { value: "en", label: "EN" },
      { value: "ar", label: "AR" },
      { value: "tr", label: "TR" },
    ],
    []
  );

  const goToAnchor = (id) => (e) => {
    setMenuOpen(false);

    const languageHome = localizedPath("/", lang);
    if (location.pathname !== languageHome) {
      e.preventDefault();
      navigate({ pathname: languageHome, hash: `#${id}` });
      return;
    }

    setActiveId(id);

    const isSameHash = window.location.hash === `#${id}` || (id === "top" && !window.location.hash);
    if (isSameHash) {
      e.preventDefault();
      if (id === "top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        scrollToSection(id);
      }
    }
  };

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname, location.hash]);

  // Close on outside click
  useEffect(() => {
    const onDocDown = (e) => {
      if (!langRef.current) return;
      if (!langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  // Close on Escape (both menus)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setLangOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Handle hash navigation while staying on "/"
  useEffect(() => {
    if (location.pathname !== localizedPath("/", lang)) return;

    const hash = location.hash?.replace("#", "") || "top";
    setActiveId(hash);
    activeIdRef.current = hash;
    hashLockUntilRef.current = Date.now() + 900;

    if (hash === "top") {
      const tmr = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      }, 0);
      return () => clearTimeout(tmr);
    }
  }, [location.pathname, location.hash, lang]);

  // Active link tracking while scrolling (IntersectionObserver)
  useEffect(() => {
    if (location.pathname !== localizedPath("/", lang)) return;

    const headerEl = headerRef.current;
    const headerH = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;

    const els = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;

    const visibleRatios = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < hashLockUntilRef.current) return;

        entries.forEach((entry) => {
          const id = entry.target?.id;
          if (!id) return;
          visibleRatios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
        });

        let bestId = activeIdRef.current;
        let bestRatio = -1;

        visibleRatios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });

        if (bestId && bestId !== activeIdRef.current && bestRatio > 0) {
          activeIdRef.current = bestId;
          setActiveId(bestId);
        }
      },
      {
        root: null,
        rootMargin: `-${headerH + 12}px 0px -60% 0px`,
        threshold: [0.15, 0.25, 0.35, 0.5, 0.65],
      }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname, sectionIds, lang]);

  const isActive = (id) => (activeId === id ? "is-active" : "");

  const currentLangLabel = (langItems.find((x) => x.value === lang) || langItems[0]).label;

  const setLanguage = (value) => {
    setLang(value);
    setLangOpen(false);
  };

  return (
    <header ref={headerRef} className="nv finisher-header" dir={dir}>
      <a className="nv__skip" href="#main">
        Skip to content
      </a>

      <div className="container nv__inner">
        {/* LEFT */}
        <a className="nv__brand" href="#top" onClick={goToAnchor("top")} aria-label="Go to top">
          <img src={Logo} alt="QuadroCode logo" className="nv__logo" width="38" height="38" />
          <span className="nv__name">QuadroCode</span>
        </a>

        {/* CENTER */}
        <nav className="nv__nav" aria-label="Primary">
          {navItems.map((it) => (
            <a
              key={it.id}
              className={`nv__link ${isActive(it.id)}`}
              href={`#${it.id}`}
              onClick={goToAnchor(it.id)}
            >
              {it.label}
            </a>
          ))}
        </nav>

        {/* RIGHT */}
        <div className="nv__actions">
          <a className={`nv__cta ${isActive("contact")}`} href="#contact" onClick={goToAnchor("contact")}>
            {t.nav.contact}
          </a>

          {/* Custom Language Dropdown */}
          <div className="nv__langWrap" ref={langRef}>
            <button
              type="button"
              className={`nv__langBtn ${langOpen ? "is-open" : ""}`}
              aria-haspopup="menu"
              aria-expanded={langOpen ? "true" : "false"}
              onClick={() => setLangOpen((v) => !v)}
            >
              <Globe className="nv__langIcon" aria-hidden="true" focusable="false" size={16} />
              <span className="nv__langText">{currentLangLabel}</span>
              <span className="nv__chev" aria-hidden="true" />
            </button>

            <div className={`nv__langMenu ${langOpen ? "is-open" : ""}`} role="menu">
              {langItems.map((it) => (
                <button
                  key={it.value}
                  type="button"
                  role="menuitem"
                  className={`nv__langItem ${it.value === lang ? "is-active" : ""}`}
                  onClick={() => setLanguage(it.value)}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="nv__burger"
            aria-label="Open menu"
            aria-expanded={menuOpen ? "true" : "false"}
            aria-controls="nv-mobile"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className={`nv__burgerIcon ${menuOpen ? "is-open" : ""}`} aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div id="nv-mobile" className={`nv__mobile ${menuOpen ? "is-open" : ""}`}>
        <div className="container nv__mobileInner">
          <nav className="nv__mobileNav" aria-label="Mobile primary">
            {navItems.map((it) => (
              <a
                key={it.id}
                className={`nv__mobileLink ${isActive(it.id)}`}
                href={`#${it.id}`}
                onClick={goToAnchor(it.id)}
              >
                {it.label}
              </a>
            ))}

            <a className={`nv__mobileLink ${isActive("contact")}`} href="#contact" onClick={goToAnchor("contact")}>
              {t.nav.contact}
            </a>
          </nav>

          <div className="nv__mobileRow">
            <span className="nv__mobileLabel">Language</span>
            <div className="nv__mobileLang">
              {langItems.map((it) => (
                <button
                  key={it.value}
                  type="button"
                  className={`nv__mobileLangBtn ${it.value === lang ? "is-active" : ""}`}
                  onClick={() => setLanguage(it.value)}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
